import React, { useEffect, useRef } from "react";

// Marquee component: measures container & text width and sets CSS variables
// to run a pixel-accurate marquee animation that scrolls fully before repeating.
// Props:
// - text: string to render and scroll
// - pixelsPerSecond (optional): speed, defaults to 120 px/s

export default function Marquee({ text, pixelsPerSecond = 90 }) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const resizeObserverRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;

    const compute = () => {
      // ensure inline-block measured width
      const containerWidth = Math.max(container.clientWidth, 0);
      const textWidth = Math.max(textEl.scrollWidth, textEl.offsetWidth, 0);

      // If text is shorter than container, we still want to move it a small distance
      // but keep it visually stable; set duration so it moves slowly and repeats.
      const distance = containerWidth + textWidth;
  const durationSeconds = Math.max(distance / pixelsPerSecond, 6);

      // set CSS variables used by index.css
      textEl.style.setProperty("--marquee-duration", `${durationSeconds}s`);
      textEl.style.setProperty("--marquee-from", `${containerWidth}px`);
      textEl.style.setProperty("--marquee-to", `-${textWidth}px`);

      // re-trigger animation by toggling the 'animate' class
      textEl.classList.remove("animate");
      // force reflow to restart animation
      // eslint-disable-next-line no-unused-expressions
      textEl.offsetHeight;
      textEl.classList.add("animate");
    };

    compute();

    // Observe resizes so we can recompute when the container changes
    if (window.ResizeObserver) {
      resizeObserverRef.current = new ResizeObserver(() => compute());
      resizeObserverRef.current.observe(container);
      resizeObserverRef.current.observe(textEl);
    } else {
      // fallback: window resize
      window.addEventListener("resize", compute);
    }

    // Also recompute after a short delay in case fonts/images changed layout
    const t = setTimeout(compute, 200);

    return () => {
      clearTimeout(t);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      } else {
        window.removeEventListener("resize", compute);
      }
    };
  }, [text, pixelsPerSecond]);

  return (
    <div ref={containerRef} className="marquee-container w-full h-6 flex items-center overflow-hidden">
      <div ref={textRef} className="marquee-text">{text}</div>
    </div>
  );
}
